import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, Search, Filter, Eye,
  CheckCircle, XCircle, Clock, AlertTriangle, RefreshCw,
  Calendar, Download
} from 'lucide-react';
import api from '../../utils/api';
import { downloadInvoice } from '../../utils/invoiceDownload';
import MobileInvoicePreview from '../portal/MobileInvoicePreview';

const MobileInvoiceOversight = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [invoicesPerPage] = useState(20);
  
  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, statusFilter]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: invoicesPerPage,
        search: searchQuery,
        status: statusFilter !== 'all' ? statusFilter : '',
        sortBy: 'created_at',
        sortOrder: 'DESC'
      });

      const response = await api.get(`/api/dashboard/staff/invoices?${params}`);
      
      if (response.data.success) {
        const invoicesArray = response.data.data || [];
        setInvoices(invoicesArray);
        setTotalInvoices(response.data.pagination?.total || invoicesArray.length);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      await downloadInvoice(invoice, 'pdf', { setLoading, preferStaffEndpoint: true });
    } catch (error) {
      console.warn('Mobile oversight invoice download failed, using legacy renderer', error);
      await legacyDownloadInvoice(invoice);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchInvoices();
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'GBP') => {
    if (!amount) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'pending_approval': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'overdue': return 'bg-orange-100 text-orange-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'pending_approval': return <AlertTriangle className="w-4 h-4" />;
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'overdue': return <AlertTriangle className="w-4 h-4" />;
      case 'draft': return <FileText className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      invoice.invoiceNumber?.toLowerCase().includes(query) ||
      invoice.invoice_number?.toLowerCase().includes(query) ||
      invoice.sender?.companyName?.toLowerCase().includes(query) ||
      invoice.receiver?.companyName?.toLowerCase().includes(query)
    );
  });

  const totalPages = Math.ceil(totalInvoices / invoicesPerPage);

  // Handle view invoice
  const handleViewInvoice = async (invoice) => {
    setPreviewLoading(true);
    setShowViewModal(true);
    try {
      const response = await api.get(`/api/staff/invoices/${invoice.id}`);
      setViewingInvoice(response.data?.invoice || response.data?.data || invoice);
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setViewingInvoice(invoice);
    } finally {
      setPreviewLoading(false);
    }
  };

  const legacyDownloadInvoice = async (invoice) => {
    try {
      const response = await api.get(`/api/invoices/${invoice.id}/download-pdf`, {
        responseType: 'blob',
        timeout: 30000
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invoice.invoiceNumber || invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Invoice Oversight</h1>
              <p className="text-sm text-gray-600 mt-1">Monitor all system invoices</p>
            </div>
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className="p-4 space-y-3">
          <div className="flex items-center space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg border transition-colors ${
                showFilters || statusFilter !== 'all'
                  ? 'bg-[#1F11ED] text-white border-[#1F11ED]'
                  : 'bg-white text-gray-600 border-gray-300'
              }`}
            >
              <Filter className="w-5 h-5" />
            </button>
            <button
              onClick={fetchInvoices}
              className="p-2 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-3"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED] focus:border-transparent"
                >
                  <option value="all">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  <option value="pending">Pending</option>
                  <option value="pending_approval">Pending Approval</option>
                  <option value="approved">Approved</option>
                  <option value="paid">Paid</option>
                  <option value="rejected">Rejected</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
              <button
                onClick={() => {
                  setStatusFilter('all');
                  setSearchQuery('');
                  setShowFilters(false);
                }}
                className="w-full text-sm text-gray-600 py-2"
              >
                Clear Filters
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Invoice List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-36 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : filteredInvoices.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
            <p className="text-sm text-gray-600">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'No invoices yet'}
            </p>
          </div>
        ) : (
          <AnimatePresence>
            {filteredInvoices.map((invoice, index) => (
              <motion.div
                key={invoice.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 text-lg mb-1">
                        {invoice.invoiceNumber || invoice.invoice_number}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {invoice.sender?.companyName || invoice.sender?.company_name || 
                         `${invoice.sender?.firstName} ${invoice.sender?.lastName}` || 'N/A'}
                        {' → '}
                        {invoice.receiver?.companyName || invoice.receiver?.company_name ||
                         `${invoice.receiver?.firstName} ${invoice.receiver?.lastName}` ||
                         invoice.guestRecipient?.companyName || 'Guest'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(invoice.total, invoice.currency)}
                      </p>
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        <span>{(invoice.status || 'pending').replace('_', ' ').toUpperCase()}</span>
                      </span>
                    </div>
                  </div>

                  {/* Invoice Details */}
                  <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                    <div>
                      <span className="text-gray-500">Created:</span>
                      <p className="font-medium text-gray-900">{formatDate(invoice.createdAt || invoice.created_at)}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Due Date:</span>
                      <p className="font-medium text-gray-900">{formatDate(invoice.dueDate || invoice.due_date)}</p>
                    </div>
                    {invoice.paymentDate && (
                      <div className="col-span-2">
                        <span className="text-gray-500">Paid:</span>
                        <p className="font-medium text-green-700">{formatDate(invoice.paymentDate || invoice.payment_date)}</p>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleViewInvoice(invoice)}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <Eye className="w-4 h-4" />
                      <span className="text-sm font-medium">View</span>
                    </button>
                    <button
                      onClick={() => handleDownloadInvoice(invoice)}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                    >
                      <Download className="w-4 h-4" />
                      <span className="text-sm font-medium">Download</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 pb-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg font-medium ${
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-[#1F11ED] text-white hover:bg-blue-700'
                }`}
              >
                Previous
              </button>
              
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg font-medium ${
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-[#1F11ED] text-white hover:bg-blue-700'
                }`}
              >
                Next
              </button>
            </div>
            <div className="text-center mt-2 text-xs text-gray-500">
              {totalInvoices} total invoices
            </div>
          </div>
        </div>
      )}

      <MobileInvoicePreview
        invoice={viewingInvoice}
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        onDownloadPDF={() => viewingInvoice && handleDownloadInvoice(viewingInvoice)}
        loading={previewLoading}
        title="Invoice Details"
        viewType="staff"
      />
    </div>
  );
};

export default MobileInvoiceOversight;
